// src/components/maps/MapTab.js

import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import { createPortal } from 'react-dom';

import {
  Link,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import {
  LuRoute,
} from 'react-icons/lu';

import {
  FaTruckFast,
} from 'react-icons/fa6';

import {
  canManageRouteTemplates,
} from '../../lib/routePermissions';

const SLIDE_DURATION = 210;

const MapTabs = () => {
  const { t } = useTranslation('map');

  const location = useLocation();
  const navigate = useNavigate();

  const hasRouteTemplateAccess =
    canManageRouteTemplates();

  const activeView =
    location.pathname.includes('/map/routes')
      ? 'routes'
      : 'live';

  const [visualView, setVisualView] =
    useState(activeView);

  const navigationTimerRef = useRef(null);

  useEffect(() => {
    setVisualView(activeView);

    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }
  }, [activeView]);

  useEffect(() => {
    return () => {
      if (navigationTimerRef.current) {
        clearTimeout(navigationTimerRef.current);
      }
    };
  }, []);

  const handleTabClick = (event, nextView, path) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();

    if (navigationTimerRef.current) {
      clearTimeout(navigationTimerRef.current);
      navigationTimerRef.current = null;
    }

    setVisualView(nextView);

    if (nextView === activeView) {
      return;
    }

    navigationTimerRef.current = setTimeout(() => {
      navigationTimerRef.current = null;
      navigate(path);
    }, SLIDE_DURATION);
  };

  const containerStyle = {
    position: 'fixed',
    top: '12px',
    left: '50%',
    transform: 'translateX(-50%)',

    zIndex: 1200,

    width: hasRouteTemplateAccess
      ? '306px'
      : '158px',

    maxWidth: 'calc(100vw - 24px)',
    height: '42px',

    display: 'grid',

    gridTemplateColumns: hasRouteTemplateAccess
      ? 'repeat(2, minmax(0, 1fr))'
      : 'minmax(0, 1fr)',

    alignItems: 'center',
    gap: '4px',

    padding: '4px',
    margin: 0,

    boxSizing: 'border-box',

    border: '1px solid rgba(255, 255, 255, 0.16)',
    borderRadius: '999px',

    background: '#141a22',

    boxShadow:
      '0 8px 24px rgba(0, 0, 0, 0.25), ' +
      'inset 0 1px 0 rgba(255, 255, 255, 0.05)',

    backdropFilter: 'blur(14px)',
    WebkitBackdropFilter: 'blur(14px)',

    isolation: 'isolate',

    opacity: 1,
    visibility: 'visible',
    pointerEvents: 'auto',
  };

  const sliderStyle = {
    position: 'absolute',

    top: '4px',
    bottom: '4px',
    left: '4px',

    width: hasRouteTemplateAccess
      ? 'calc((100% - 12px) / 2)'
      : 'calc(100% - 8px)',

    zIndex: 0,

    boxSizing: 'border-box',

    border: '1px solid rgba(255, 255, 255, 0.10)',
    borderRadius: '999px',

    background:
      'linear-gradient(180deg, #3b4653, #303b47)',

    boxShadow:
      '0 3px 9px rgba(0, 0, 0, 0.25), ' +
      'inset 0 1px 0 rgba(255, 255, 255, 0.07)',

    opacity: 1,

    pointerEvents: 'none',

    transform:
      hasRouteTemplateAccess &&
      visualView === 'routes'
        ? 'translateX(calc(100% + 4px))'
        : 'translateX(0)',

    transition:
      `transform ${SLIDE_DURATION}ms cubic-bezier(0.22, 1, 0.36, 1)`,

    willChange: 'transform',
  };

  const getItemStyle = (view) => ({
    position: 'relative',
    zIndex: 1,

    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',

    gap: '6px',

    minWidth: 0,
    height: '32px',

    padding: '0 4px',

    border: 0,
    borderRadius: '999px',

    background: 'transparent',

    color:
      visualView === view
        ? '#ffffff'
        : '#b9c5d5',

    opacity: 1,

    fontFamily: 'inherit',
    fontSize: '11px',
    fontWeight:
      visualView === view
        ? 750
        : 650,

    lineHeight: 1.2,

    textAlign: 'center',
    textDecoration: 'none',
    whiteSpace: 'nowrap',

    cursor: 'pointer',

    transition:
      `color ${SLIDE_DURATION}ms ease`,

    userSelect: 'none',
  });

  const getIconStyle = (view) => ({
    width: '15px',
    height: '15px',

    flex: '0 0 15px',

    color:
      visualView === view
        ? '#60a5fa'
        : '#a0aec2',

    filter: 'none',

    transition:
      `color ${SLIDE_DURATION}ms ease, ` +
      `filter ${SLIDE_DURATION}ms ease`,
  });

  const navigation = (
    <nav
      className="loadevo-map-tabs-fixed"
      style={containerStyle}
      aria-label={t('map.tabs.navigation', {
        defaultValue: 'Navegação do mapa',
      })}
    >
      <span
        className="loadevo-map-tabs-fixed__slider"
        style={sliderStyle}
        data-view={visualView}
        aria-hidden="true"
      />

      <Link
        to="/map/live"
        onClick={(event) =>
          handleTabClick(
            event,
            'live',
            '/map/live'
          )
        }
        className={
          visualView === 'live'
            ? 'loadevo-map-tabs-fixed__item is-active'
            : 'loadevo-map-tabs-fixed__item'
        }
        style={getItemStyle('live')}
      >
        <FaTruckFast
          aria-hidden="true"
          style={{
            ...getIconStyle('live'),
            width: '19px',
            height: '19px',
            flex: '0 0 19px',
          }}
        />

        <span>
          {t('map.tabs.liveTransports', {
            defaultValue: 'Transportes atuais',
          })}
        </span>
      </Link>

      {hasRouteTemplateAccess && (
        <Link
          to="/map/routes"
          onClick={(event) =>
            handleTabClick(
              event,
              'routes',
              '/map/routes'
            )
          }
          className={
            visualView === 'routes'
              ? 'loadevo-map-tabs-fixed__item is-active'
              : 'loadevo-map-tabs-fixed__item'
          }
          style={getItemStyle('routes')}
        >
          <LuRoute
            aria-hidden="true"
            style={getIconStyle('routes')}
          />

          <span>
            {t('map.tabs.routes', {
              defaultValue: 'Rotas',
            })}
          </span>
        </Link>
      )}
    </nav>
  );

  if (typeof document === 'undefined') {
    return null;
  }

  return createPortal(
    navigation,
    document.body
  );
};

export default MapTabs;